import {
  ActionError,
  AttentionBlock,
  Button,
  Checkbox,
  ChoiceRow,
  ConfirmDialog,
  EmptyState,
  Field,
  FieldGrid,
  FormBlock,
  Input,
  FormFooter,
  PageHeader,
  PageTitle,
  ReadingColumn,
  ReorderControls,
  SavedIndicator,
  Skeleton,
  StatusTag,
  Surface,
  Textarea,
  toast,
  useBreakpoint,
} from '@westhetics/ui'
import { useMemo, useState, type ReactNode } from 'react'
import { Link, useNavigate, useParams } from 'react-router'
import { LoadError } from '@/app/load-error'
import { Page } from '@/app/shell'
import { isApiError } from '@/lib/api'
import { errorMessage, fieldErrors } from '@/lib/errors'
import { routes } from '@/routes'
import {
  useCreateQuestionnaireVersion,
  usePublishQuestionnaireVersion,
  useQuestionnaireVersion,
  useQuestionnaireVersions,
  useUpdateQuestionnaireVersion,
  type QuestionnaireVersion,
} from './api'
import { QuestionForm } from './question-form'
import { ConfirmationReadBlock, QuestionRow, SectionReadBlock } from './question-summary'
import {
  MSG,
  draftAsQuestion,
  emptyQuestion,
  fromVersion,
  moveItem,
  serverErrorsAt,
  takenKeysExcept,
  toVersionInput,
  validateDeclaration,
  validateQuestion,
  validateSection,
  type FieldErrorMap,
  type QuestionDraft,
  type SectionDraft,
  type VersionDraft,
} from './questionnaire-form'
import {
  ABOUT_YOU_NOTE,
  SECTION_NAMES,
  publishIssueSentence,
  questionCountLine,
  versionPublicationLine,
  versionStatus,
  versionTitle,
} from './questionnaire-labels'

/**
 * Uma versão do questionário padrão (docs/areas/anamnese.md, "Telas · Questionário", P-ANA-66;
 * RN-ANA-18). Arquétipo: cadastro, de root. O rascunho se edita em blocos — declaração, origem e
 * os quatro blocos do desenho — e se publica num bloco próprio no fim, com conferência e
 * justificativa. A versão publicada é só leitura: corrigir é criar um rascunho a partir dela.
 */

const SECTION_LIMIT = 20

export function QuestionnaireVersionPage() {
  const { id = '' } = useParams()
  const query = useQuestionnaireVersion(id)
  const versions = useQuestionnaireVersions()
  const isDesk = useBreakpoint('desk')
  const version = query.data
  const draft = versions.data?.data.find((item) => item.status === 'draft')

  if (query.isError || !version) {
    return (
      <>
        <Header label="…" />
        <Page>
          <ReadingColumn wide>
            {query.isError ? (
              <LoadError
                title="Não conseguimos abrir esta versão"
                error={query.error}
                onRetry={() => void query.refetch()}
              />
            ) : (
              <div role="status" aria-label="Carregando" className="flex flex-col gap-4">
                <Skeleton className="h-[26px] w-[240px]" />
                <Skeleton className="h-[13px] w-[420px] max-w-full" />
                <Skeleton className="mt-4 h-[180px] w-full" />
                <Skeleton className="h-[320px] w-full" />
                <Skeleton className="h-[320px] w-full" />
              </div>
            )}
          </ReadingColumn>
        </Page>
      </>
    )
  }

  if (version.status === 'draft' && isDesk) {
    return <DraftEditor key={version.id} version={version} />
  }
  return (
    <VersionReader
      version={version}
      editable={isDesk}
      existingDraftId={draft?.id ?? null}
      draftsKnown={Boolean(versions.data)}
    />
  )
}

function Header({
  label,
  aside,
  actions,
}: {
  label: string
  aside?: ReactNode
  actions?: ReactNode
}) {
  return (
    <PageHeader
      LinkComponent={Link}
      crumbs={[
        { label: 'Westhetics' },
        { label: 'Questionário padrão', href: routes.admin.questionnaire },
        { label },
      ]}
      aside={aside}
      actions={actions}
    />
  )
}

function Title({ version, count }: { version: QuestionnaireVersion; count: number }) {
  const status = versionStatus(version)
  const parts = [versionPublicationLine(version), questionCountLine(count)]
  if (version.status === 'published') parts.push(version.isCurrent ? 'vigente' : 'anterior')
  return (
    <PageTitle
      title={versionTitle(version)}
      meta={parts.join(' · ')}
      trailing={<StatusTag level={status.level}>{status.label}</StatusTag>}
    />
  )
}

// Leitura ------------------------------------------------------------------------------------------

function VersionReader({
  version,
  editable,
  existingDraftId,
  draftsKnown,
}: {
  version: QuestionnaireVersion
  editable: boolean
  existingDraftId: string | null
  /** A lista de versões já respondeu: dá para saber se existe rascunho. */
  draftsKnown: boolean
}) {
  const navigate = useNavigate()
  const create = useCreateQuestionnaireVersion()
  const sections = version.sections ?? []
  const isPublished = version.status === 'published'

  return (
    <>
      <Header
        label={versionTitle(version)}
        actions={
          isPublished && editable && draftsKnown ? (
            <Button
              size="sm"
              disabled={Boolean(existingDraftId)}
              loading={create.isPending}
              loadingLabel="Criando o rascunho…"
              onClick={() =>
                create.mutate(
                  { basedOnVersionId: version.id },
                  {
                    onSuccess: (created) => navigate(routes.admin.questionnaireVersion(created.id)),
                  },
                )
              }
            >
              {existingDraftId ? 'Já existe um rascunho' : 'Criar rascunho a partir desta'}
            </Button>
          ) : null
        }
      />
      <Page>
        <ReadingColumn wide>
          <Title version={version} count={version.questionCount} />
          <div className="mt-6 flex flex-col gap-4">
            {!editable ? (
              <AttentionBlock
                variant="lilac"
                title="A edição do questionário continua no computador"
              >
                Aqui a versão aparece em leitura. Para editar o rascunho ou criar um a partir desta,
                abra numa janela com mais de 900px.
              </AttentionBlock>
            ) : null}
            {existingDraftId && isPublished && editable ? (
              <AttentionBlock variant="lilac" title="Há um rascunho em edição">
                Só existe um rascunho por vez.{' '}
                <Link
                  className="border-ink border-b"
                  to={routes.admin.questionnaireVersion(existingDraftId)}
                >
                  Abrir o rascunho
                </Link>
              </AttentionBlock>
            ) : null}
            {create.isError ? (
              <ActionError
                title="O rascunho não foi criado"
                description={errorMessage(create.error)}
                onDismiss={() => create.reset()}
              />
            ) : null}

            {sections.map((section, index) => (
              <SectionReadBlock key={section.key} section={section} index={index} showReview />
            ))}
            <ConfirmationReadBlock
              declarationText={version.declaration.text}
              index={sections.length}
            />

            <Surface className="max-desk:px-4">
              <h2 className="eyebrow">{isPublished ? 'Publicação' : 'Rascunho'}</h2>
              <dl className="font-body text-body text-ink mt-3 grid grid-cols-[max-content_1fr] gap-x-6 gap-y-2 font-light">
                <dt className="text-secondary">Declaração</dt>
                <dd>versão {version.declaration.version ?? '—'}</dd>
                {isPublished ? (
                  <>
                    <dt className="text-secondary">Publicada</dt>
                    <dd>{versionPublicationLine(version)}</dd>
                    <dt className="text-secondary">Justificativa</dt>
                    <dd>{version.publishJustification ?? '—'}</dd>
                  </>
                ) : null}
                <dt className="text-secondary">Origem</dt>
                <dd>{version.source ?? '—'}</dd>
              </dl>
            </Surface>
          </div>
        </ReadingColumn>
      </Page>
    </>
  )
}

// Edição -------------------------------------------------------------------------------------------

/** O que ainda impede publicar, pelas mesmas regras da API (`publishWarnings`), por extenso. */
function localPublishIssues(draft: VersionDraft) {
  const issues: string[] = []
  for (const section of draft.sections) {
    const name = section.name.trim() || SECTION_NAMES[section.key]
    if (section.key !== 'aboutYou' && section.questions.length === 0) {
      issues.push(`${name}: falta pelo menos uma pergunta`)
    }
    for (const question of section.questions) {
      const missing = [
        ...(question.review.legal ? [] : ['jurídica']),
        ...(question.review.clinical ? [] : ['clínica']),
      ]
      if (missing.length > 0) {
        issues.push(
          `${name} · "${question.label.trim() || 'pergunta sem texto'}": falta revisão ${missing.join(' e ')}`,
        )
      }
    }
  }
  return issues
}

function DraftEditor({ version }: { version: QuestionnaireVersion }) {
  const navigate = useNavigate()
  const update = useUpdateQuestionnaireVersion(version.id)
  const publish = usePublishQuestionnaireVersion(version.id)

  const [draft, setDraft] = useState<VersionDraft>(() => fromVersion(version))
  const [dirty, setDirty] = useState(false)
  const [submitted, setSubmitted] = useState(false)
  const [touched, setTouched] = useState<Set<string>>(() => new Set())
  const [expanded, setExpanded] = useState<string | null>(null)
  const [savedAt, setSavedAt] = useState<Date | null>(() => new Date(version.updatedAt))
  const [serverWarnings, setServerWarnings] = useState<string[] | null>(null)
  const [justification, setJustification] = useState('')
  const [confirming, setConfirming] = useState(false)

  function change(patch: Partial<VersionDraft>) {
    setDraft((current) => ({ ...current, ...patch }))
    setDirty(true)
    if (update.isError) update.reset()
  }
  function changeSection(index: number, patch: Partial<SectionDraft>) {
    change({
      sections: draft.sections.map((section, i) =>
        i === index ? { ...section, ...patch } : section,
      ),
    })
  }
  const touch = (path: string) =>
    setTouched((current) => (current.has(path) ? current : new Set(current).add(path)))

  // O que a tela confere antes de salvar, com as mensagens da API.
  const declarationErrors = useMemo(() => validateDeclaration(draft), [draft])
  const sectionErrors = useMemo(() => draft.sections.map(validateSection), [draft])
  const questionProblems = useMemo(
    () =>
      draft.sections.flatMap((section) =>
        section.questions
          .filter(
            (question, q) =>
              Object.keys(
                validateQuestion(question, {
                  isClinic: false,
                  previous: section.questions.slice(0, q),
                  takenKeys: takenKeysExcept(draft.sections, question.uid),
                }),
              ).length > 0,
          )
          .map((question) => question.uid),
      ),
    [draft],
  )
  const serverFields = update.isError ? fieldErrors(update.error) : {}
  const errorAt = (path: string, own: string | undefined) =>
    (submitted || touched.has(path) ? own : undefined) ?? serverFields[path]

  const count = draft.sections.reduce((sum, section) => sum + section.questions.length, 0)
  const issues = dirty || serverWarnings === null ? localPublishIssues(draft) : serverWarnings
  const justificationError =
    justification.trim().length < 10 || justification.trim().length > 500 ? MSG.justification : null

  function save() {
    setSubmitted(true)
    const invalid =
      Object.keys(declarationErrors).length > 0 ||
      sectionErrors.some((errors) => Object.keys(errors).length > 0) ||
      questionProblems.length > 0
    if (invalid) {
      if (questionProblems[0]) setExpanded(questionProblems[0])
      return
    }
    update.mutate(toVersionInput(draft), {
      onSuccess: (saved) => {
        toast('Rascunho salvo')
        setDirty(false)
        setSubmitted(false)
        setSavedAt(new Date(saved.updatedAt))
        setServerWarnings(
          saved.warnings.map((warning) => publishIssueSentence(warning, saved.sections)),
        )
      },
      onError: (error) => {
        // O 422 aponta a pergunta por índices: ela abre com a mensagem embaixo do campo.
        for (const path of Object.keys(fieldErrors(error))) {
          const match = /^sections\.(\d+)\.questions\.(\d+)/.exec(path)
          if (!match) continue
          const uid = draft.sections[Number(match[1])]?.questions[Number(match[2])]?.uid
          if (uid) {
            setExpanded(uid)
            return
          }
        }
      },
    })
  }

  function confirmPublish() {
    publish.mutate(
      { justification: justification.trim() },
      {
        onSuccess: (published) => {
          setConfirming(false)
          toast(`Versão ${published.number} publicada — vale nas próximas anamneses`)
          navigate(routes.admin.questionnaire)
        },
        onError: () => setConfirming(false),
      },
    )
  }

  const updateConflict =
    update.isError && isApiError(update.error) && update.error.code === 'conflict'
  const hasPlacedFieldError = Object.keys(serverFields).some(
    (path) => path.startsWith('sections.') || path.startsWith('declaration.') || path === 'source',
  )
  const publishFields = publish.isError ? fieldErrors(publish.error) : {}
  const publishBlocked = dirty
    ? 'Salve o rascunho antes de publicar'
    : issues.length > 0
      ? `Resolva ${issues.length === 1 ? '1 aviso' : `os ${issues.length} avisos`} para publicar`
      : justificationError
        ? 'Escreva a justificativa para publicar'
        : null

  return (
    <>
      <Header label="Rascunho" />
      <Page>
        <ReadingColumn wide>
          <Title version={version} count={count} />

          <div className="mt-6 flex flex-col gap-4">
            {updateConflict ? (
              <ActionError
                title="Esta versão não aceita mais edição"
                description={errorMessage(update.error)}
                actions={
                  <Button variant="outline" size="sm" asChild>
                    <Link to={routes.admin.questionnaire}>Ver as versões</Link>
                  </Button>
                }
              />
            ) : update.isError && !hasPlacedFieldError ? (
              <ActionError
                title="O rascunho não foi salvo"
                description={`${errorMessage(update.error)} O que você alterou continua na tela.`}
                onDismiss={() => update.reset()}
              />
            ) : null}

            <FormBlock
              title="Declaração"
              note="É o que a paciente aceita antes de enviar e o que fica na prova. Muda só com versão nova do questionário."
            >
              <div className="flex flex-col gap-4">
                <FieldGrid>
                  <Field
                    label="Versão da declaração"
                    error={errorAt('declaration.version', declarationErrors.declarationVersion)}
                    hint="Como 2026-09. A paciente envia esta versão junto com o aceite."
                  >
                    <Input
                      value={draft.declarationVersion}
                      maxLength={20}
                      onChange={(event) => change({ declarationVersion: event.target.value })}
                      onBlur={() => touch('declaration.version')}
                    />
                  </Field>
                </FieldGrid>
                <Field
                  label="Texto da declaração"
                  meta={`${draft.declarationText.trim().length}/2000`}
                  error={errorAt('declaration.text', declarationErrors.declarationText)}
                >
                  <Textarea
                    rows={4}
                    value={draft.declarationText}
                    maxLength={2000}
                    onChange={(event) => change({ declarationText: event.target.value })}
                    onBlur={() => touch('declaration.text')}
                  />
                </Field>
                <Field
                  label="Origem do conteúdo"
                  optional
                  error={errorAt('source', declarationErrors.source)}
                  hint="De onde veio e quem revisou, como 'base v3 do legado, exportada em …, revisada por …'."
                >
                  <Textarea
                    rows={2}
                    value={draft.source}
                    maxLength={500}
                    onChange={(event) => change({ source: event.target.value })}
                    onBlur={() => touch('source')}
                  />
                </Field>
              </div>
            </FormBlock>

            {draft.sections.map((section, s) => (
              <SectionEditor
                key={section.key}
                section={section}
                index={s}
                errors={sectionErrors[s] ?? {}}
                errorAt={(path, own) => errorAt(`sections.${s}.${path}`, own)}
                touch={(path) => touch(`sections.${s}.${path}`)}
                onChange={(patch) => changeSection(s, patch)}
                expanded={expanded}
                onExpand={setExpanded}
                submitted={submitted}
                allSections={draft.sections}
                serverErrorsFor={(q) =>
                  serverErrorsAt(serverFields, `sections.${s}.questions.${q}.`)
                }
              />
            ))}

            <FormFooter
              status={<SavedIndicator savedAt={savedAt} dirty={dirty} saving={update.isPending} />}
              primary={
                <Button
                  disabled={!dirty}
                  loading={update.isPending}
                  loadingLabel="Salvando o rascunho…"
                  onClick={save}
                >
                  {dirty ? 'Salvar rascunho' : 'Sem alterações para salvar'}
                </Button>
              }
            />

            <Surface className="max-desk:px-4" aria-labelledby="publish-title">
              <h2 id="publish-title" className="eyebrow">
                Publicar esta versão
              </h2>
              <p className="font-body text-secondary mt-[6px] text-[12px] leading-[1.6] font-light">
                Passa a valer para toda anamnese enviada a partir de então, em todas as clínicas.
                Uma versão publicada não se edita: corrigir é publicar outra.
              </p>
              <div className="mt-4 flex flex-col gap-4">
                {issues.length > 0 ? (
                  <AttentionBlock
                    variant="plum"
                    tone="pampas"
                    title={
                      issues.length === 1
                        ? '1 aviso impede a publicação'
                        : `${issues.length} avisos impedem a publicação`
                    }
                  >
                    <ul>
                      {issues.map((issue) => (
                        <li key={issue}>· {issue}</li>
                      ))}
                    </ul>
                  </AttentionBlock>
                ) : (
                  <AttentionBlock
                    variant="lilac"
                    tone="pampas"
                    title="O rascunho está pronto para publicar"
                  >
                    Todo bloco tem pergunta e toda pergunta tem revisão jurídica e clínica. Confira
                    a declaração e os textos: depois de publicada, a versão não muda.
                  </AttentionBlock>
                )}

                {publish.isError ? (
                  <ActionError
                    title="A versão não foi publicada"
                    description={
                      <>
                        {errorMessage(publish.error)}
                        {Object.keys(publishFields).length > 0 ? (
                          <ul className="mt-2">
                            {Object.entries(publishFields).map(([path, message]) => (
                              <li key={path}>
                                ·{' '}
                                {publishIssueSentence(
                                  `${path}: ${message}`,
                                  version.sections ?? [],
                                )}
                              </li>
                            ))}
                          </ul>
                        ) : null}
                      </>
                    }
                    onDismiss={() => publish.reset()}
                  />
                ) : null}

                <Field
                  label="Justificativa"
                  meta={`${justification.trim().length}/500`}
                  error={
                    (touched.has('justification') && justificationError) ||
                    publishFields.justification
                  }
                  hint="Entra na trilha: por que esta versão existe e o que mudou."
                >
                  <Textarea
                    rows={2}
                    value={justification}
                    maxLength={500}
                    onChange={(event) => setJustification(event.target.value)}
                    onBlur={() => touch('justification')}
                  />
                </Field>
                <div className="flex justify-end">
                  <Button
                    variant="outline"
                    disabled={publishBlocked !== null}
                    onClick={() => setConfirming(true)}
                  >
                    {publishBlocked ?? 'Publicar versão'}
                  </Button>
                </div>
              </div>
            </Surface>
          </div>
        </ReadingColumn>
      </Page>

      <ConfirmDialog
        open={confirming}
        onOpenChange={setConfirming}
        tone="primary"
        title="Publicar esta versão do questionário?"
        effect="Ela passa a valer para toda anamnese enviada a partir de agora, em todas as clínicas. As anamneses já enviadas, inclusive as não preenchidas, continuam na versão de então. Uma versão publicada não se edita: corrigir é publicar outra."
        confirmLabel="Publicar"
        loading={publish.isPending}
        loadingLabel="Publicando a versão…"
        onConfirm={confirmPublish}
      />
    </>
  )
}

function SectionEditor({
  section,
  index,
  errors,
  errorAt,
  touch,
  onChange,
  expanded,
  onExpand,
  submitted,
  allSections,
  serverErrorsFor,
}: {
  section: SectionDraft
  index: number
  errors: FieldErrorMap
  errorAt: (path: string, own: string | undefined) => string | undefined
  touch: (path: string) => void
  onChange: (patch: Partial<SectionDraft>) => void
  expanded: string | null
  onExpand: (uid: string | null) => void
  submitted: boolean
  allSections: readonly SectionDraft[]
  serverErrorsFor: (questionIndex: number) => FieldErrorMap
}) {
  const isAboutYou = section.key === 'aboutYou'
  const asQuestions = section.questions.map((draft, q) => draftAsQuestion(draft, q + 1))
  const name = section.name.trim() || SECTION_NAMES[section.key]

  function setQuestions(questions: QuestionDraft[]) {
    onChange({ questions })
  }
  function add() {
    const question = emptyQuestion('yes_no')
    setQuestions([...section.questions, question])
    onExpand(question.uid)
  }

  return (
    <FormBlock
      title={`${index + 1} · ${name}`}
      note={isAboutYou ? ABOUT_YOU_NOTE : undefined}
      aria-label={`Bloco ${index + 1} · ${name}`}
    >
      <div className="flex flex-col gap-4">
        <FieldGrid>
          <Field label="Nome do bloco" error={errorAt('name', errors.name)}>
            <Input
              value={section.name}
              maxLength={80}
              onChange={(event) => onChange({ name: event.target.value })}
              onBlur={() => touch('name')}
            />
          </Field>
        </FieldGrid>
        {isAboutYou ? null : (
          <>
            <Field
              label="Introdução"
              optional={!section.isSensitive}
              meta={`${section.intro.trim().length}/300`}
              error={errorAt('intro', errors.intro)}
              hint="A frase que abre o bloco na tela, antes da primeira pergunta."
            >
              <Textarea
                rows={2}
                value={section.intro}
                maxLength={300}
                onChange={(event) => onChange({ intro: event.target.value })}
                onBlur={() => touch('intro')}
              />
            </Field>
            <ChoiceRow
              control={
                <Checkbox
                  checked={section.isSensitive}
                  onCheckedChange={(checked) => onChange({ isSensitive: checked === true })}
                />
              }
              description="Toda pergunta dele pode ser pulada e a introdução aparece antes da primeira."
            >
              Bloco sensível
            </ChoiceRow>

            <section
              aria-label={`Perguntas do bloco ${name}`}
              className="border-hairline border-t pt-4"
            >
              <div className="flex items-baseline justify-between gap-3">
                <h3 className="eyebrow">
                  Perguntas · {section.questions.length} de {SECTION_LIMIT}
                </h3>
              </div>
              {errorAt('questions', undefined) ? (
                <p
                  role="alert"
                  className="font-body text-eyebrow text-plum mt-2 leading-[1.5] font-light"
                >
                  {errorAt('questions', undefined)}
                </p>
              ) : null}
              {section.questions.length === 0 ? (
                <EmptyState
                  className="mt-3"
                  title="Nenhuma pergunta neste bloco"
                  description="Cada bloco de perguntas precisa de pelo menos uma antes de publicar."
                  action={<Button onClick={add}>+ Adicionar pergunta</Button>}
                />
              ) : (
                <div role="list" className="mt-2">
                  {section.questions.map((draft, q) => {
                    const question = asQuestions[q]!
                    if (expanded === draft.uid) {
                      return (
                        <div key={draft.uid} role="listitem" className="py-3">
                          <QuestionForm
                            question={draft}
                            mode="default"
                            previous={section.questions.slice(0, q)}
                            takenKeys={takenKeysExcept(allSections, draft.uid)}
                            serverErrors={serverErrorsFor(q)}
                            showAllErrors={submitted}
                            onChange={(next) =>
                              setQuestions(
                                section.questions.map((item) =>
                                  item.uid === next.uid ? next : item,
                                ),
                              )
                            }
                            onClose={() => onExpand(null)}
                            onRemove={() => {
                              setQuestions(
                                section.questions.filter((item) => item.uid !== draft.uid),
                              )
                              onExpand(null)
                            }}
                          />
                        </div>
                      )
                    }
                    const label = question.label || 'pergunta sem texto'
                    return (
                      <QuestionRow
                        key={draft.uid}
                        question={question}
                        siblings={asQuestions}
                        showReview
                        className="px-0"
                        actions={
                          <>
                            <Button
                              variant="text"
                              size="sm"
                              aria-label={`Editar "${label}"`}
                              onClick={() => onExpand(draft.uid)}
                            >
                              editar
                            </Button>
                            <ReorderControls
                              index={q}
                              count={section.questions.length}
                              itemLabel={label}
                              onMove={(from, to) =>
                                setQuestions(moveItem(section.questions, from, to))
                              }
                            />
                            <Button
                              variant="text"
                              size="sm"
                              aria-label={`Remover "${label}"`}
                              onClick={() =>
                                setQuestions(
                                  section.questions.filter((item) => item.uid !== draft.uid),
                                )
                              }
                            >
                              remover
                            </Button>
                          </>
                        }
                      />
                    )
                  })}
                </div>
              )}
              {section.questions.length > 0 ? (
                <div className="mt-3">
                  <Button
                    variant="outline"
                    size="sm"
                    disabled={section.questions.length >= SECTION_LIMIT}
                    onClick={add}
                  >
                    {section.questions.length >= SECTION_LIMIT
                      ? `Limite de ${SECTION_LIMIT} perguntas por bloco`
                      : '+ Adicionar pergunta'}
                  </Button>
                </div>
              ) : null}
            </section>
          </>
        )}
      </div>
    </FormBlock>
  )
}
